import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, FileText } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATES = {
  quote: {
    name: 'Quotation',
    fields: ['client_name', 'quote_number', 'items', 'subtotal', 'vat', 'total', 'valid_until', 'notes']
  },
  tax_invoice: {
    name: 'Tax Invoice',
    fields: ['client_name', 'invoice_number', 'items', 'subtotal', 'vat', 'total', 'due_date', 'banking_details']
  },
  purchase_order: {
    name: 'Purchase Order',
    fields: ['supplier_name', 'po_number', 'items', 'delivery_address', 'delivery_date', 'terms']
  },
  service_agreement: {
    name: 'Service Agreement',
    fields: ['client_name', 'service_description', 'start_date', 'end_date', 'fee', 'terms', 'signatures']
  },
  supplier_onboarding: {
    name: 'Supplier Onboarding',
    fields: ['supplier_name', 'contact_person', 'registration_number', 'vat_number', 'bank_details', 'bee_status']
  },
  client_onboarding: {
    name: 'Client Onboarding',
    fields: ['client_name', 'contact_person', 'registration_number', 'vat_number', 'address', 'industry']
  }
};

export function Templates() {
  const [selectedTemplate, setSelectedTemplate] = useState('quote');
  const [data, setData] = useState({});
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/templates/generate`,
        { template_type: selectedTemplate, data },
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedTemplate}_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Document generated');
    } catch (error) {
      toast.error('Failed to generate document');
    } finally {
      setGenerating(false);
    }
  };

  const currentTemplate = TEMPLATES[selectedTemplate];

  return (
    <div className="p-8" data-testid="templates-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}} data-testid="templates-title">Document Templates</h1>
        <p className="mt-2 text-zinc-600">Generate professional documents from templates</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-zinc-200">
          <div className="p-6 border-b border-zinc-200">
            <h2 className="text-xl font-semibold" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Templates</h2>
          </div>
          <div className="divide-y divide-zinc-200" data-testid="templates-list">
            {Object.entries(TEMPLATES).map(([key, template]) => (
              <button
                key={key}
                onClick={() => { setSelectedTemplate(key); setData({}); }}
                className={`w-full p-4 text-left transition-colors flex items-center gap-3 ${selectedTemplate === key ? 'bg-black text-white' : 'hover:bg-zinc-50'}`}
                data-testid={`template-${key}`}
              >
                <FileText size={20} />
                <span className="font-medium">{template.name}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-zinc-200">
          <div className="p-6 border-b border-zinc-200">
            <h2 className="text-xl font-semibold" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>{currentTemplate.name}</h2>
          </div>
          <div className="p-6 space-y-4" data-testid="template-form">
            {currentTemplate.fields.map((field) => (
              <div key={field}>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">
                  {field.replace(/_/g, ' ')}
                </Label>
                {field === 'items' || field === 'terms' || field === 'notes' || field === 'service_description' ? (
                  <Textarea
                    value={data[field] || ''}
                    onChange={(e) => setData({ ...data, [field]: e.target.value })}
                    rows={3}
                    data-testid={`template-field-${field}`}
                    className="mt-2 border-zinc-300 hover:border-black transition-colors"
                  />
                ) : (
                  <Input
                    value={data[field] || ''}
                    onChange={(e) => setData({ ...data, [field]: e.target.value })}
                    data-testid={`template-field-${field}`}
                    className="mt-2 border-zinc-300 hover:border-black transition-colors"
                  />
                )}
              </div>
            ))}
            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="bg-black text-white hover:bg-zinc-800 gap-2"
              data-testid="generate-template-button"
            >
              <Download size={18} />
              {generating ? 'Generating...' : 'Generate PDF'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
